import { useToast } from "@bcc/hooks/use-toast";
import { getApiErrorMessage } from "@bcc/lib/utils";
import { useInstallationsState } from "@client-shared/providers/installations";
import { putInstallation } from "@client-shared/services/api/installations";
import type { Installation } from "@shared/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface UseUpdateOrderConfigurationResult {
  readonly isSaving: boolean;
  readonly saveOrderConfiguration: (installation: Installation) => Promise<void>;
}

/**
 * Persists order configuration (`prefs.orderPrefix` / `prefs.orderTags`) for an
 * installation via the existing `/bc-installations` PUT endpoint, surfaces the
 * result as a toast, and refreshes the shared InstallationsProvider so reads
 * reflect the saved values.
 */
export function useUpdateOrderConfiguration(): UseUpdateOrderConfigurationResult {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [, , installationMethods] = useInstallationsState();

  const saveOrderConfiguration = useCallback(
    async (installation: Installation) => {
      setIsSaving(true);
      try {
        await putInstallation(installation);
        toast({
          variant: "success",
          title: t("views.preferences.orderConfiguration.saveSuccessTitle"),
          description: t("views.preferences.orderConfiguration.saveSuccessDescription"),
        });
        // Refresh the shared provider so the in-memory installation map
        // reflects the saved prefix and tags.
        installationMethods?.fetch();
      } catch (error: unknown) {
        toast({
          variant: "destructive",
          title: t("views.preferences.orderConfiguration.saveErrorTitle"),
          description: getApiErrorMessage(error, t("views.preferences.orderConfiguration.saveErrorDescription") as string),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [toast, t, installationMethods]
  );

  return { isSaving, saveOrderConfiguration };
}
